import {
    Grid,
    LinearProgress,
    MenuItem,
    Select,
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import TableRow from "@mui/material/TableRow";
import CatalogCheckbox from "./EnhancedCheckbox";
import TableSortLabel from "@mui/material/TableSortLabel";
import {ExpandMore, ChevronLeft, ChevronRight} from "@mui/icons-material";
import { Pagination } from '@mui/material';
import React from "react";
import IconButton from "@mui/material/IconButton";

const Header = styled(Typography)({
    fontWeight: "700",
    fontSize: "14px",
});

const StyledPagination = styled(Pagination)({
    color: "#4F4D55",
});

const TopPagination = styled('div')({
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#4F4D55",
});

const PaginationButton = styled(IconButton)({
    padding: "4px",
    color: "#4F4D55",
    "&:disabled": {
        color: "#BEBEBE",
    }
});

const CurrentPageBox = styled('div')(({ theme }) => ({
    border: `1px solid ${theme.palette.tertiary?.main || theme.palette.primary.main}`,
    borderRadius: "4px",
    padding: "4px",
    color: theme.palette.tertiary?.main || theme.palette.primary.main,
    fontSize: "14px",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxSizing: "border-box",
}));

const TableContainer = styled(Grid)({
    backgroundColor: "white",
    borderRadius: "6px",
    overflow: "auto",
    width: "100%"
});

const TableActions = styled(Grid)({
    display: "flex",
    flex: "none",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "16px 12px",
    position: "sticky",
    left: 0,
});

const TableExtensions = styled(Grid)({
    display: "flex",
    flex: 0.2,
    justifyContent: "flex-start",
    alignItems: "center",
    padding: "16px 12px",
    position: "sticky",
    left: 0,
});

interface CatalogManagerTableProps {
    table: any;
    config: any;
    isSelectable?: boolean;
    bulkActions?: React.ReactNode;
    extensions?: React.ReactNode;
    hasInlineFilters?: boolean;
    hideTopPagination?: boolean;
    stickyHeader?: boolean;
    moreTableOptions?: any;
    tableMessage?: React.ReactNode;
    generateRows?: () => any;
    forceRefresh?: any;
    containerStyle?: React.CSSProperties;
    [key: string]: any;
}

function CatalogManagerTable(props: CatalogManagerTableProps) {
    const theme = useTheme();
    const loading = props.table.state.loading;

    let selectable = props.config.selectable;
    if (props.isSelectable !== undefined) selectable = props.isSelectable;

    // CatalogManagerTable Pagination Calculations //
    let startingIndex = (props.table.state.page * props.table.state.rowsPerPage);
    let endingIndex = Math.min(startingIndex + props.table.state.rowsPerPage, props.table.state.total);
    
    // Handle case when there's no data
    const hasData = props.table.state.total > 0;

    // Format CatalogManagerTable Data Into Rows //
    const displayRows = React.useMemo(() => props.generateRows(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(props.table.data), JSON.stringify(props.table.state.selected), JSON.stringify(props.table.state.columnSettings), props.forceRefresh]);

    return (
        <>
            {(selectable || props.bulkActions) && (
                <TableActions item container style={{backgroundColor: "white", borderRadius: "6px 6px 0 0", width: "100%", justifyContent: "space-between"}}>
                    <Grid item size={6} container alignItems="center">
                        {selectable && (
                            <Typography
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: theme.palette.textSecondary.main,
                                    marginRight: "12px"
                                }}>
                                {props.table.state.selected.length} Selected
                            </Typography>
                        )}
                        {props.table.state.selected.length > 0 && props.bulkActions}
                    </Grid>
                    <Grid item size={6} container justifyContent="flex-end">
                        {hasData && !props.hasInlineFilters && !props.hideTopPagination && (
                            <TopPagination>
                                <PaginationButton
                                    onClick={(e) => props.table.extensions.handleChangePage(e, props.table.state.page - 1)}
                                    disabled={props.table.state.page === 0}
                                    size="small"
                                >
                                    <ChevronLeft />
                                </PaginationButton>
                                <CurrentPageBox>
                                    {props.table.state.page + 1}
                                </CurrentPageBox>
                                <PaginationButton
                                    onClick={(e) => props.table.extensions.handleChangePage(e, props.table.state.page + 1)}
                                    disabled={props.table.state.page >= Math.ceil(props.table.state.total / props.table.state.rowsPerPage) - 1}
                                    size="small"
                                >
                                    <ChevronRight />
                                </PaginationButton>
                            </TopPagination>
                        )}
                    </Grid>
                </TableActions>
            )}

            {!(selectable || props.bulkActions) && hasData && !props.hasInlineFilters && !props.hideTopPagination && (
                <TableActions item container style={{backgroundColor: "white", borderRadius: "6px 6px 0 0"}}>
                    <Grid item size={12} container justifyContent="flex-end">
                        <TopPagination>
                            <PaginationButton
                                onClick={(e) => props.table.extensions.handleChangePage(e, props.table.state.page - 1)}
                                disabled={props.table.state.page === 0}
                                size="small"
                            >
                                <ChevronLeft />
                            </PaginationButton>
                            <CurrentPageBox>
                                {props.table.state.page + 1}
                            </CurrentPageBox>
                            <PaginationButton
                                onClick={(e) => props.table.extensions.handleChangePage(e, props.table.state.page + 1)}
                                disabled={props.table.state.page >= Math.ceil(props.table.state.total / props.table.state.rowsPerPage) - 1}
                                size="small"
                            >
                                <ChevronRight />
                            </PaginationButton>
                        </TopPagination>
                    </Grid>
                </TableActions>
            )}

            {props.extensions && (
                <TableExtensions style={{backgroundColor: "white"}}>
                    {props.extensions}
                </TableExtensions>
            )}
            
            <TableContainer item container style={{...props.containerStyle, borderRadius: hasData && !props.hasInlineFilters ? "0" : "6px"}}>

                <LinearProgress
                    style={{
                        width: props.stickyHeader ? "calc(100% - 48px)" : "100%",
                        maxWidth: "100vw",
                        height: '0.2em',
                        visibility: loading ? undefined : 'hidden',
                        position: props.stickyHeader ? 'absolute' : undefined,
                        zIndex: props.stickyHeader ? "10000" : undefined
                    }}/>

                <Table sx={{ backgroundColor: "white", borderRadius: "6px", minWidth: 650, width: "100%" }} stickyHeader={props.stickyHeader}>
                    <TableHead sx={(theme) => ({ backgroundColor: theme.palette.background.paper })}>
                        <TableRow>
                            {selectable && (
                                <TableCell padding="checkbox" style={{width: '3em'}}>
                                    <CatalogCheckbox
                                        checked={props.table.state.data.length > 0 && props.table.state.selected.length === props.table.state.data.length}
                                        indeterminate={props.table.state.selected.length > 0 && props.table.state.selected.length < props.table.state.data.length}
                                        onChange={(e) => props.table.extensions.handleSelectAllClick(e)}
                                    />
                                </TableCell>
                            )}
                            {props.config.columns.map((column, index) => {
                                if (props.table.extensions.isColumnHidden(column)) return null;
                                if (column.hidden && !column.toggleable) return null;

                                return (
                                    <TableCell key={index}
                                               style={{
                                                   width: column.width,
                                                   minWidth: column.width,
                                                   maxWidth: column.width,
                                                   whiteSpace: "nowrap",
                                                   overflow: "hidden",
                                                   textOverflow: "ellipsis",
                                                   textAlign: 'left',
                                                   padding: selectable ? "0" : "4px 0px 4px 24px",
                                                   ...column.cellStyle,
                                               }}
                                    >
                                        <Header>
                                            {column.sortable ? (
                                                <Tooltip title="Sort" placement='bottom-start' enterDelay={300}>
                                                    <TableSortLabel
                                                        active={props.table.state.orderBy === column.id}
                                                        direction={props.table.state.order}
                                                        onClick={(event) => props.table.extensions.handleRequestSort(event, column.id)}
                                                    >
                                                        {column.label}
                                                    </TableSortLabel>
                                                </Tooltip>
                                            ) : (
                                                <>{column.label}</>
                                            )}
                                        </Header>
                                    </TableCell>
                                )
                            })}

                            {props.moreTableOptions && (
                                <TableCell style={{padding: 0}}></TableCell>
                            )}

                            {props.config.columns.filter(col => col.empty === true).map(
                                (column, index) => {
                                    return <TableCell key={index} style={{padding: 0}}></TableCell>
                                }
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.tableMessage ? props.tableMessage : (<>{displayRows}</>)}
                    </TableBody>
                </Table>
            </TableContainer>

            <TableActions item container justifyContent="flex-end" flexWrap="nowrap" style={{backgroundColor: "white", borderRadius: "0 0 6px 6px"}}>
                <Grid size={6} item container alignItems="center" flexWrap="nowrap" style={{gap: "8px", flex: 1}}>
                    <Typography style={{color: "#4F4D55", whiteSpace: "nowrap"}}>
                        Rows per page
                    </Typography>
                    <Select value={props.table.state.rowsPerPage} disableUnderline
                            onChange={(e) => props.table.extensions.handleChangeRowsPerPage(e)}
                            IconComponent={(props) => (<ExpandMore {...props}/>)}
                            size="small"
                            sx={{
                                '& .MuiSelect-select': { paddingY: '4px' },
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', borderRadius: '4px' }
                            }}>
                        {props.config.pageSizes.map((pageSize, index) => {
                            return (
                                <MenuItem key={index} value={pageSize}>{pageSize}</MenuItem>
                            )
                        })}
                    </Select>
                </Grid>

                <Grid size={6} item container alignItems="center" justifyContent="flex-end" flexWrap="nowrap" style={{width: "100%", paddingRight: "8px"}}>
                    <Typography style={{color: "#4F4D55"}}>
                        {hasData ? (
                            <>Showing <b>{Math.min(startingIndex + 1, props.table.state.total)}</b> to <b>{endingIndex}</b> of <b>{props.table.state.total}</b> Results</>
                        ) : (
                            <>No results found</>
                        )}
                    </Typography>
                    {hasData && (
                        <StyledPagination
                            shape="rounded"
                            count={Math.ceil(props.table.state.total / props.table.state.rowsPerPage)}
                            page={props.table.state.page + 1}
                            onChange={(e, page) => props.table.extensions.handleChangePage(e, page - 1)}
                        />
                    )}
                </Grid>
            </TableActions>
        </>
    );
}

export default CatalogManagerTable;
